// One "Pay" the person means to make once is one ATTEMPT, and an attempt has
// one key (`Idempotency-Key`). The server answers the same key once: a double
// tap, or a request a flaky network sends again, is not charged twice
// (blhWebServer src/platform/PLATFORM.md, "Idempotency").
//
// The rule this file holds, and nothing else:
//
//   - the same selection, tried again after a network failure, a server
//     failure (5xx) or "already being processed" (409 REQUEST_IN_PROGRESS),
//     is the SAME attempt: same key, so the server and Stripe can recognise it;
//   - a definitive answer — paid, or refused for a reason of its own (a
//     declined card, a changed balance) — ends the attempt: the next "Pay" is
//     a new one, with a new key;
//   - "we could not confirm whether this went through" (409
//     REQUEST_OUTCOME_UNKNOWN, PAYMENT_MAY_HAVE_GONE_THROUGH) also ends it, and
//     is never retried on its own: the person sees their refreshed balance
//     first, and a deliberate new "Pay" is a new attempt;
//   - a different selection (other items, amounts or card) is a different
//     attempt: the server refuses one key for two different requests.

export type AttemptOutcome = "success" | "definitive" | "retry_same" | "check_first";

const CHECK_FIRST_CODES = new Set(["REQUEST_OUTCOME_UNKNOWN", "PAYMENT_MAY_HAVE_GONE_THROUGH"]);

/** What a failed request means for its attempt. */
export function outcomeOfError(error: { status?: number; code?: string } | null | undefined): AttemptOutcome {
  const status = Number(error?.status || 0);
  const code = error?.code;
  if (code && CHECK_FIRST_CODES.has(code)) return "check_first";
  if (status === 0 || status >= 500) return "retry_same";
  if (status === 409 && code === "REQUEST_IN_PROGRESS") return "retry_same";
  return "definitive";
}

/** Whether an attempt's answer means the balance must be read again before anything else. */
export function mustCheckFirst(error: { status?: number; code?: string } | null | undefined): boolean {
  return outcomeOfError(error) === "check_first";
}

function randomKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  // Older browsers: 32 hex characters from the platform's random source.
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

/** A stable text of a value: object keys sorted, so the same selection reads the same. */
function fingerprintOf(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(fingerprintOf).join(",")}]`;
  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    return `{${Object.keys(record)
      .sort()
      .filter((key) => record[key] !== undefined)
      .map((key) => `${JSON.stringify(key)}:${fingerprintOf(record[key])}`)
      .join(",")}}`;
  }
  return JSON.stringify(value ?? null);
}

export type PaymentAttempt = {
  /** The key for this selection: the current attempt's, or a new attempt's. */
  keyFor(selection: unknown): string;
  /** Records how the request ended; a finished attempt gives its key up. */
  settle(outcome: AttemptOutcome): void;
};

export function createPaymentAttempt(mint: () => string = randomKey): PaymentAttempt {
  let key: string | null = null;
  let selectionPrint: string | null = null;
  return {
    keyFor(selection) {
      const print = fingerprintOf(selection);
      if (!key || print !== selectionPrint) {
        key = mint();
        selectionPrint = print;
      }
      return key;
    },
    settle(outcome) {
      if (outcome !== "retry_same") {
        key = null;
        selectionPrint = null;
      }
    },
  };
}
